/**
 * To jest główny moduł (kompilujący się do pliku index.js)
 * Ten moduł ładuje inne moduły, w tym główne moduły
 * Aby załadować nim jakiś komponent(y), należy w linku do skryptu index.js dodać parametr index.js?components=nazwa_komponentu1,nazwa_komponentu2
 * Najlepiej też do html dodać linijkę np. <link rel="modulepreload" href="/bookingengine/dist/assets/NfHotelBookingEngineModal.9b258f34.js">
 *     żeby dany komponent od razu się zaczął ładować zanim zostanie użyty
 * Dostępne nazwy komponentów głównych: modal (do .nf-be-modal-root)
 * Dostępne nazwy podkomponentów: menu (do .nf-be-menu-root)
 */

import {App, createApp, Directive} from 'vue';
import translateDirective from './directives/translate';
import swiperDirective from './directives/swiper';
import onVisibleDirective from './directives/onvisible';
import formFieldDirective from './directives/formField';
import NfInputWrapper from "@/vueComponents/NfInputWrapper.vue";
import NfFormWrapper from "@/vueComponents/NfFormWrapper.vue";
import NfCanvas from "@/vueComponents/NfCanvas.vue";
import NfIcon from "@/vueComponents/NfIcon.vue";
import NfFlag from '@/vueComponents/NfFlag.vue';
import nfTel from "@/directives/nfTel";
import NfPostCode from "@/directives/nfPostCode";
import intlStyle from 'intl-tel-input/build/css/intlTelInput.css';
import type {NfHotelMainComponentBase} from "@/components/mainComponents/NfHotelMainComponentBase";
import NfAccordion from "@/vueComponents/NfAccordion.vue";
import NfTimer from "@/vueComponents/NfTimer.vue";
import NfAvailabilityCalendar from "@/vueComponents/NfAvailabilityCalendar.vue";

const style = document.createElement('style');
style.innerHTML = intlStyle;
document.head.appendChild(style);

if(window.localStorage.getItem('nfShowDebugMessages')) {
    window.nfShowDebugMessages = true;
}

/**
 * Ta funkcja będzie czekała, aż wymagane biblioteki się załadują (lodash i moment aktualnie)
 */
async function waitForLibraries() {
    return new Promise<void>((resolve,reject) => {
        const startTime = new Date().getTime();
        const intervalId = setInterval(() => {
            if(window['_'] && window['moment']) {
                clearInterval(intervalId);
                resolve();
            } else {
                const currentTime = new Date().getTime();
                if(currentTime - startTime > 30000) {
                    clearInterval(intervalId);
                    reject('Nie załadowano lodasha lub momentu po 30 sekundach');
                }
            }
        },10);
    });
}

/**
 * Dla każdego importowalnego modułu musi być poniżej linijka ładująca go asynchronicznie
 * Te linijki poniżej służą tylko dla kompilatora żeby wiedział jakie moduły ma wydzielić
 */
async function loadComponent(name: string, resolve: (app:App<Element>) => void) {
    await waitForLibraries();
    let component = null as object|null;
    switch (name) {
//region komponenty
        case 'modal':
            component = await import('./components/mainComponents/NfHotelBookingEngineModal');
            break;
        case 'componentHost':
            component = await import('./components/mainComponents/NfHotelStandAloneComponent');
            break;
//endregion
        default:
            throw Error("Unknown main component: "+name);
    }
    //@ts-ignore
    const app = createApp((component.default as NfHotelMainComponentBase).getAppFunction());
    app.directive('translate',<Directive><unknown>translateDirective);
    app.directive('onVisible',onVisibleDirective);
    app.directive('formField',formFieldDirective);
    app.directive('nfTel', nfTel);
    app.directive('nfPostCode', NfPostCode);
    app.directive('swiper',<Directive><unknown>swiperDirective);
    app.component('NfInputWrapper', NfInputWrapper);
    app.component('NfFormWrapper', NfFormWrapper);
    app.component('NfCanvas', NfCanvas);
    app.component('NfIcon', NfIcon);
    app.component('NfFlag', NfFlag);
    app.component('NfTimer',NfTimer);
    app.component('NfAccordion', NfAccordion);
    app.component('NfAvailabilityCalendar', NfAvailabilityCalendar);
    window.nfHotelComponentResolvedApps[name] = true;
    //app.config.unwrapInjectedRef = true;
    resolve(app);
}
const allComponentNames = ['modal','componentHost'];
var componentResolvers: { [componentName:string]: (app:App<Element> )=> void  } = {
};
const componentNames = window.nfHotelComponents ;
if(componentNames) {
    window.nfHotelComponentsApps = {};
    window.nfHotelComponentResolvedApps = {};
    window.nfHotelComponentsInitialized = false;
    for (let componentName of allComponentNames) {
        window.nfHotelComponentsApps[componentName] =  new Promise<App<Element>>((resolve) => {
            componentResolvers[componentName] = resolve;
        });
    }
    const _componentNames = componentNames.split(',');
    const initialComponents = [];
    for (const componentName of _componentNames) {
        initialComponents.push(loadComponent(componentName.trim(), componentResolvers[componentName.trim()]));
    }
    Promise.all(initialComponents).then(() => {
        window.nfHotelComponentsInitialized = true;
    });
}
window.resetNfHotelApp = async function(componentName: string) {
    if(componentName) {
        window.nfHotelComponentsApps[componentName] = new Promise<App<Element>>(resolve => {
            componentResolvers[componentName] = resolve;
        });
        await loadComponent(componentName, componentResolvers[componentName]);
    }
}

window.removeNfHotelApp = async function (componentName: string): Promise<void> {
    const app = await window.nfHotelComponentsApps[componentName];
    app.unmount();
    delete window.nfHotelComponentsApps[componentName];
    delete window.nfHotelComponentResolvedApps[componentName];
}

window.provideNfHotelApp = async function (componentName: string, name: string, value: any): Promise<void> {
    const app = await window.nfHotelComponentsApps[componentName];
    app.provide(name,value);
}

window.getNfAppPromise = async function (componentName: string): Promise<App<Element>> {
    if (window.nfHotelComponentsApps.hasOwnProperty(componentName)) {
        const result = window.nfHotelComponentsApps[componentName];
        delete window.nfHotelComponentsApps[componentName];
        return result;
    }

    return new Promise<App<Element>>((resolve) => {
        loadComponent(componentName, resolve);
    })
}
